import type { ServiceResponse } from './types';
import { apiRequest } from '../utils/api';
import { API_ENDPOINTS } from '../config/api.config';
import { createCachedRequest } from '../utils/requestDeduplication';

// Backend response type
interface BrandResponseDto {
  _id: string;
  id: string;
  name: string;
  slug: string;
  description?: string;
  logoUrl?: string;
  websiteUrl?: string;
  isActive: boolean;
  isFeatured: boolean;
  displayOrder: number;
  productsCount?: number;
  createdAt: string;
  updatedAt: string;
}

// Frontend type
export interface Brand {
  id: string;
  name: string;
  slug: string;
  description?: string;
  logo?: string;
  website?: string;
  productsCount?: number;
  isFeatured?: boolean;
}

class BrandService {
  // Transform backend brand to frontend format
  private transformBrand(brand: BrandResponseDto): Brand {
    return {
      id: brand._id || brand.id,
      name: brand.name,
      slug: brand.slug,
      description: brand.description,
      logo: brand.logoUrl,
      website: brand.websiteUrl,
      productsCount: brand.productsCount,
      isFeatured: brand.isFeatured
    };
  }

  // Internal method to fetch brands
  private async _getBrands(): Promise<ServiceResponse<Brand[]>> {
    try {
      const brands = await apiRequest<BrandResponseDto[]>(API_ENDPOINTS.BRANDS.BASE);
      
      // Filter active brands and transform
      const transformedBrands = brands
        .filter(brand => brand.isActive)
        .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name))
        .map(brand => this.transformBrand(brand));

      return {
        success: true,
        data: transformedBrands
      };
    } catch (error) {
      console.error('Error fetching brands:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch brands'
      };
    }
  }

  // Get featured brands
  async getFeaturedBrands(): Promise<ServiceResponse<Brand[]>> {
    try {
      const brandsResponse = await this.getBrands();
      if (!brandsResponse.success) {
        return brandsResponse;
      }

      const featuredBrands = brandsResponse.data.filter(brand => brand.isFeatured);

      return {
        success: true,
        data: featuredBrands
      };
    } catch (error) {
      console.error('Error fetching featured brands:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch featured brands'
      };
    }
  }

  // Get brand by slug
  async getBrandBySlug(slug: string): Promise<ServiceResponse<Brand | null>> {
    try {
      const brand = await apiRequest<BrandResponseDto>(API_ENDPOINTS.BRANDS.BY_SLUG(slug));
      
      if (!brand || !brand.isActive) {
        return {
          success: false,
          data: null,
          error: 'Brand not found'
        };
      }

      return {
        success: true,
        data: this.transformBrand(brand)
      };
    } catch (error) {
      console.error('Error fetching brand by slug:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch brand'
      };
    }
  }

  // Get brand by ID
  async getBrandById(id: string): Promise<ServiceResponse<Brand | null>> {
    try {
      const brand = await apiRequest<BrandResponseDto>(API_ENDPOINTS.BRANDS.BY_ID(id));
      
      if (!brand || !brand.isActive) {
        return {
          success: false,
          data: null,
          error: 'Brand not found'
        };
      }

      return {
        success: true,
        data: this.transformBrand(brand)
      };
    } catch (error) {
      console.error('Error fetching brand by ID:', error);
      return {
        success: false,
        data: null,
        error: error instanceof Error ? error.message : 'Failed to fetch brand'
      };
    }
  }

  // Get brands with product count above threshold
  async getPopularBrands(minProducts: number = 5): Promise<ServiceResponse<Brand[]>> {
    try {
      const brandsResponse = await this.getBrands();
      if (!brandsResponse.success) {
        return brandsResponse;
      }

      const popularBrands = brandsResponse.data
        .filter(brand => (brand.productsCount || 0) >= minProducts)
        .sort((a, b) => (b.productsCount || 0) - (a.productsCount || 0));

      return {
        success: true,
        data: popularBrands
      };
    } catch (error) {
      console.error('Error fetching popular brands:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch popular brands'
      };
    }
  }
  
  // Create deduped version of getBrands
  getBrands = createCachedRequest(
    this._getBrands.bind(this),
    () => 'brands-all',
    60000 // Cache for 1 minute
  );
}

export const brandService = new BrandService();